import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { adminApi, type UserSummary } from '../lib/api.js';
import type { CurrentUser } from '../lib/auth.js';
import { CreateUserModal } from '../components/CreateUserModal.js';
import { UserRow } from '../components/UserRow.js';

export function Admin({ user }: { user: CurrentUser }) {
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const isAdmin = user.groups.includes('auth:admin');

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.listUsers(),
    enabled: isAdmin,
  });

  const { data: groupsData, error: groupsError } = useQuery({
    queryKey: ['groups'],
    queryFn: () => adminApi.listGroups(),
    enabled: isAdmin,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createMutation = useMutation({
    mutationFn: (input: { email: string; name: string; groups: string[] }) =>
      adminApi.createUser(input),
    onSuccess: () => {
      setCreateOpen(false);
      refresh();
    },
  });

  const allGroups = groupsData?.groups ?? [];

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-6xl mx-auto px-12 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-display text-brand-800">User management</h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 text-sm font-display font-medium"
        >
          + Add user
        </button>
      </div>

      {isLoading && <div className="text-brand-500">Loading…</div>}
      {error && (
        <div className="text-red-700 mb-4">
          Failed to load users: {String(error)}
        </div>
      )}
      {groupsError && (
        <div className="text-red-700 mb-4">
          Failed to load groups: {String(groupsError)}
        </div>
      )}

      {data && (
        <div className="border border-brand-100">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-left">
              <tr>
                <th className="px-3 py-2 font-display text-brand-700">Email</th>
                <th className="px-3 py-2 font-display text-brand-700">Name</th>
                <th className="px-3 py-2 font-display text-brand-700">Status</th>
                <th className="px-3 py-2 font-display text-brand-700">Groups</th>
                <th className="px-3 py-2 font-display text-brand-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u: UserSummary) => (
                <UserRow
                  key={u.sub}
                  user={u}
                  allGroups={allGroups}
                  onChange={refresh}
                />
              ))}
            </tbody>
          </table>
          {data.users.length === 0 && (
            <div className="px-3 py-4 text-brand-500">No users yet. Click "Add user" to create one.</div>
          )}
        </div>
      )}

      {createOpen && (
        <CreateUserModal
          allGroups={allGroups}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(input) => createMutation.mutate(input)}
          submitting={createMutation.isPending}
          error={createMutation.error instanceof Error ? createMutation.error.message : null}
        />
      )}
    </div>
  );
}
